import { useMemo, useCallback, useRef, useState } from "react";
import { StepProps } from "@/types/broker-registration.types";
import { FormField } from "../FormField";
import { TextInput } from "../TextInput";
import { SelectInput } from "../SelectInput";
import { RadioGroup } from "../RadioGroup";
import { CheckboxGroup } from "../CheckboxGroup";
import { AddressAutocomplete } from "../AddressAutocomplete";
import { PasswordInput } from "../PasswordInput";
import { ALL_LANGUAGES, COUNTRIES } from "@/constants/broker-registration.constants";
import { useLanguage } from "@/contexts/LanguageContext";
import { Camera, Upload, X, User } from "lucide-react";

export function StepOne({ formData, updateFormData, errors, clearError }: StepProps) {
  const { t } = useLanguage();
  const fileInputRef = useRef<HTMLInputElement>(null);
  const [photoPreview, setPhotoPreview] = useState<string | null>(null);

  const languageOptions = ALL_LANGUAGES.map((lang) => ({
    value: lang,
    label: t(`broker_registration.options.languages.${lang}`),
  }));
  
  const handlePhotoUpload = (event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];
    if (file) {
      if (file.size > 5 * 1024 * 1024) {
        alert(t("broker_registration.step_one.photo_size_error") || "File too large. Max 5MB.");
        return;
      }
      updateFormData({ profilePhoto: file });
      const reader = new FileReader();
      reader.onloadend = () => {
        setPhotoPreview(reader.result as string);
      };
      reader.readAsDataURL(file);
    }
  };

  const removePhoto = () => {
    updateFormData({ profilePhoto: null });
    setPhotoPreview(null);
    if (fileInputRef.current) {
      fileInputRef.current.value = '';
    }
  };

  const addressTypeOptions = [
    { value: "residential", label: t("broker_registration.options.address_types.residential") },
    { value: "corporate", label: t("broker_registration.options.address_types.corporate") },
  ];

  const countryOptions = COUNTRIES.map((country) => ({
    value: country.value,
    label: t(`broker_registration.options.countries.${country.value}`),
  }));

  const handleAddressSelect = useCallback(
    (components: {
      streetAddress: string;
      city: string;
      stateProvince: string;
      zipPostalCode: string;
      country: "CA" | "US" | "FR" | "UK" | "OTHER" | "";
    }) => {
      updateFormData({
        streetAddress: components.streetAddress,
        addressSearch: components.streetAddress, // Keep in sync
        city: components.city,
        stateProvince: components.stateProvince,
        zipPostalCode: components.zipPostalCode,
        country: components.country,
      });
      clearError("streetAddress");
      clearError("city");
      clearError("stateProvince");
      clearError("zipPostalCode");
      clearError("country");
    },
    [updateFormData, clearError],
  );

  return (
    <div className="space-y-6">
      <h2 className="text-xl font-semibold border-b border-slate-200 pb-3 text-[#1D314D]">
        {t("broker_registration.step_one.title")}
      </h2>

      {/* First Name and Last Name */}
      <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
        <FormField label={t("broker_registration.step_one.first_name")} error={errors.firstName} required>
          <TextInput
            value={formData.firstName}
            onChange={(value) => {
              updateFormData({ firstName: value });
              clearError("firstName");
            }}
            placeholder={t("broker_registration.step_one.first_name_placeholder")}
            error={!!errors.firstName}
          />
        </FormField>

        <FormField label={t("broker_registration.step_one.last_name")} error={errors.lastName} required>
          <TextInput
            value={formData.lastName}
            onChange={(value) => {
              updateFormData({ lastName: value });
              clearError("lastName");
            }}
            placeholder={t("broker_registration.step_one.last_name_placeholder")}
            error={!!errors.lastName}
          />
        </FormField>
      </div>

      <FormField label={t("broker_registration.step_one.firm_name")} error={errors.firmName}>
        <TextInput
          value={formData.firmName}
          onChange={(value) => {
            updateFormData({ firmName: value });
            clearError("firmName");
          }}
          placeholder={t("broker_registration.step_one.firm_name_placeholder")}
          error={!!errors.firmName}
        />
      </FormField>

      <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
        <FormField label={t("broker_registration.step_one.email")} error={errors.email} required>
          <TextInput
            type="email"
            value={formData.email}
            onChange={(value) => {
              updateFormData({ email: value });
              clearError("email");
            }}
            placeholder={t("broker_registration.step_one.email_placeholder")}
            error={!!errors.email}
          />
        </FormField>

        <FormField label={t("broker_registration.step_one.phone")} error={errors.phone} required>
          <TextInput
            type="tel"
            value={formData.phone}
            onChange={(value) => {
              updateFormData({ phone: value });
              clearError("phone");
            }}
            placeholder={t("broker_registration.step_one.phone_placeholder")}
            error={!!errors.phone}
          />
        </FormField>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
        <FormField label={t("broker_registration.step_one.password")} error={errors.password} required>
          <PasswordInput
            value={formData.password}
            onChange={(value) => {
              updateFormData({ password: value });
              clearError("password");
            }}
            placeholder={t("broker_registration.step_one.password_placeholder")}
            error={!!errors.password}
          />
        </FormField>

        <FormField label={t("broker_registration.step_one.confirm_password")} error={errors.confirmPassword} required>
          <PasswordInput
            value={formData.confirmPassword}
            onChange={(value) => {
              updateFormData({ confirmPassword: value });
              clearError("confirmPassword");
            }}
            placeholder={t("broker_registration.step_one.confirm_password_placeholder")}
            error={!!errors.confirmPassword}
          />
        </FormField>
      </div>

      <FormField label={t("broker_registration.step_one.address_type")} error={errors.addressType} required>
        <RadioGroup
          value={formData.addressType}
          onChange={(value) => {
            updateFormData({ addressType: value as "residential" | "corporate" });
            clearError("addressType");
          }}
          options={addressTypeOptions}
          name="addressType"
          error={!!errors.addressType}
        />
      </FormField>

      <FormField
        label={t("broker_registration.step_one.street_address")}
        error={errors.streetAddress}
        helperText={t("broker_registration.step_one.address_search_hint")}
        required
      >
        <AddressAutocomplete
          value={formData.streetAddress}
          onChange={(value) => {
            updateFormData({ streetAddress: value, addressSearch: value });
            clearError("streetAddress");
          }}
          onAddressSelect={handleAddressSelect}
          placeholder={t("broker_registration.step_one.address_search_placeholder")}
          error={!!errors.streetAddress}
        />
      </FormField>

      <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
        <FormField label={t("broker_registration.step_one.city")} error={errors.city} required>
          <TextInput
            value={formData.city}
            onChange={(value) => {
              updateFormData({ city: value });
              clearError("city");
            }}
            placeholder={t("broker_registration.step_one.city_placeholder")}
            error={!!errors.city}
          />
        </FormField>

        <FormField label={t("broker_registration.step_one.state_province")} error={errors.stateProvince} required>
          <TextInput
            value={formData.stateProvince}
            onChange={(value) => {
              updateFormData({ stateProvince: value });
              clearError("stateProvince");
            }}
            placeholder={t("broker_registration.step_one.state_province_placeholder")}
            error={!!errors.stateProvince}
          />
        </FormField>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
        <FormField label={t("broker_registration.step_one.zip_postal_code")} error={errors.zipPostalCode} required>
          <TextInput
            value={formData.zipPostalCode}
            onChange={(value) => {
              updateFormData({ zipPostalCode: value });
              clearError("zipPostalCode");
            }}
            placeholder={t("broker_registration.step_one.zip_postal_code_placeholder")}
            error={!!errors.zipPostalCode}
          />
        </FormField>

        <FormField label={t("broker_registration.step_one.country")} error={errors.country} required>
          <SelectInput
            value={formData.country}
            onChange={(value) => {
              updateFormData({ country: value as typeof formData.country });
              clearError("country");
            }}
            options={countryOptions}
            error={!!errors.country}
          />
        </FormField>
      </div>

      <FormField label={t("broker_registration.step_one.mother_tongue")} error={errors.motherTongue} required>
        <SelectInput
          value={formData.motherTongue}
          onChange={(value) => {
            // Clear spoken languages that match new mother tongue
            const newSpokenLanguages = formData.spokenLanguages.filter((lang) => lang !== value);
            updateFormData({ motherTongue: value, spokenLanguages: newSpokenLanguages });
            clearError("motherTongue");
          }}
          options={languageOptions}
          error={!!errors.motherTongue}
        />
      </FormField>

      <FormField
        label={t("broker_registration.step_one.spoken_languages")}
        helperText={t("broker_registration.step_one.spoken_languages_hint")}
      >
        <CheckboxGroup
          values={formData.spokenLanguages}
          onChange={(values) => updateFormData({ spokenLanguages: values })}
          options={languageOptions}
          columns={3}
        />
      </FormField>

      {/* Optional Profile Enhancement Section */}
      <div className="border-t border-slate-200 pt-6 mt-6">
        <h3 className="text-lg font-semibold text-[#1D314D] mb-4">
          {t("broker_registration.step_one.profile_enhancement") || "Profile Enhancement (Optional)"}
        </h3>
        
        {/* Profile Photo */}
        <div className="mb-6">
          <label className="block text-sm font-medium text-slate-700 mb-2">
            {t("broker_registration.step_one.profile_photo") || "Profile Photo"}
          </label>
          <p className="text-xs text-slate-500 mb-3">
            {t("broker_registration.step_one.photo_hint") || "A professional photo helps build trust with sellers and buyers. Max 5MB, JPG or PNG."}
          </p>
          
          <div className="flex items-center gap-4">
            <div className="relative">
              {photoPreview ? (
                <div className="relative">
                  <img
                    src={photoPreview}
                    alt="Profile preview"
                    className="w-24 h-24 rounded-full object-cover border-2 border-[#CEAB65]"
                  />
                  <button
                    type="button"
                    onClick={removePhoto}
                    className="absolute -top-1 -right-1 bg-red-500 text-white rounded-full p-1 hover:bg-red-600 transition"
                  >
                    <X className="w-4 h-4" />
                  </button>
                </div>
              ) : (
                <div className="w-24 h-24 rounded-full bg-slate-100 border-2 border-dashed border-slate-300 flex items-center justify-center">
                  <User className="w-10 h-10 text-slate-400" />
                </div>
              )}
            </div>
            
            <div>
              <input
                ref={fileInputRef}
                type="file"
                accept="image/jpeg,image/png"
                onChange={handlePhotoUpload}
                className="hidden"
              />
              <button
                type="button"
                onClick={() => fileInputRef.current?.click()}
                className="flex items-center gap-2 px-4 py-2 bg-slate-100 hover:bg-slate-200 rounded-lg text-sm font-medium text-slate-700 transition"
              >
                <Camera className="w-4 h-4" />
                {photoPreview 
                  ? (t("broker_registration.step_one.change_photo") || "Change Photo")
                  : (t("broker_registration.step_one.upload_photo") || "Upload Photo")
                }
              </button>
            </div>
          </div>
        </div>

        {/* Professional Description */}
        <FormField
          label={t("broker_registration.step_one.professional_description") || "Professional Description"}
          helperText={t("broker_registration.step_one.description_hint") || "Describe your experience and what makes you a trusted broker (optional)"}
        >
          <textarea
            value={formData.professionalDescription || ''}
            onChange={(e) => updateFormData({ professionalDescription: e.target.value })}
            placeholder={t("broker_registration.step_one.description_placeholder") || "Share your professional background, specializations, years of experience, and why clients should work with you..."}
            className="w-full min-h-[120px] px-4 py-3 border border-slate-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-[#CEAB65] focus:border-transparent resize-y"
            maxLength={2000}
            spellCheck={false}
          />
          <p className="text-xs text-slate-500 mt-1 text-right">
            {(formData.professionalDescription?.length || 0)}/2000
          </p>
        </FormField>
      </div>
    </div>
  );
}
